import { i18n } from '@/i18n'
import type { CsiResponse, RouletteReferralBoxPrize } from '@/types'
import type { TPlanTierValue } from '@/utils/helper-objects'
import {
  isReferralBoxPrizeCredit,
  isReferralBoxPrizePlan,
} from '@/utils/type-guard-utils'
import { toPortalUrl } from '@/utils/url-generation-utils'
import { getRandomNumber } from '@/utils/utils'
import type { DateTime } from 'luxon'

import fetcher from './fetcher'

export type ReferralBox = {
  id: number
  title: string
  code: string
  referred_customer_id: number
  opened_at: DateTime
  expired_at: DateTime
  created_at: DateTime
  updated_at: DateTime
  prizes: ReferralBoxPrize[]
  box_template: ReferralBoxTemplate
  box_template_id: number
}

export type ReferralBoxPrize = {
  id: number
  title: string
  referral_box_prizeable_type_id: ReferrablBoxPrizeableType
  locked: boolean
  post_sub_template_id: string | null
  created_at: DateTime
  updated_at: DateTime
  prizeable: ReferralBoxPrizeable
}

export enum ReferrablBoxPrizeableType {
  Credit = 1,
  XP = 2,
  Plan = 3,
  Coupon = 4,
  Reward = 5,
}

export type ReferralBoxPrizeable =
  | ReferralBoxPrizeablePlan
  | ReferralBoxPrizeableCredit

export type ReferralBoxPrizeablePlan = {
  id: number
  /** Platform plan code ID */
  platform_id: string
  /** Frontend plan code */
  code: string
  name: string
  tier: TPlanTierValue
  label: string
  category: string
  total_now: number
  total_next: number
  total_entries: string
  loyalty_entries: number
  loyalty_credit: number
  loyalty_xp: number
  trial_period_raw_days: any
  trial_period: string
  trial_period_singular: string
  billing_period: string
  active: number
  created_at: DateTime
  updated_at: DateTime
}

export type ReferralBoxPrizeableCredit = {
  id: number
  /** Amount in PENCE */
  amount: number
  /** Amount in PENCE */
  cashable: number
  created_at: DateTime
  updated_at: DateTime
}

export type ReferralBoxTemplate = {
  id: number
  code: string
  title: string
  image_path: string
  description: string
  config: ReferralBoxTemplateConfig[]
  post_register_template_id?: string
  created_at: DateTime
  updated_at: DateTime
}

export type ReferralBoxTemplateConfig =
  | ReferralBoxTemplateConfigCredit
  | ReferralBoxTemplateConfigPlan

export type ReferralBoxTemplateConfigCredit = {
  type: 'CREDIT'
  max: string
  min: string
  mode: string
  title: string
  level_ranges: ReferralBoxTemplateConfigLevelRange[]
  post_sub_template_id?: string
}

export type ReferralBoxTemplateConfigPlan = {
  type: 'PLAN'
  /** Same value returned in ReferralBoxPrizeablePlan.platform_id */
  plan_code: string
  title: string
}

export type ReferralBoxesStatsResponse = {
  total_rewards_earned_pennies: number
  unopened_boxes: string[]
  golden_boxes_sent: number
  golden_boxes_converted: number
}
export type ReferralBoxTemplateConfigLevelRange = {
  max_level: string
  max_value: string
  min_level: string
  min_value: string
}

export default class ReferralBoxService {
  public static readonly maxCashPrize: number = 5000

  /**
   * Get a referral box by template's UUID.
   *
   * @param {string} templateId
   */
  public static async getReferralBoxTemplateById(
    templateId: string
  ): Promise<ReferralBoxTemplate | null> {
    const { data, error } = await fetcher.get<CsiResponse<ReferralBoxTemplate>>(
      toPortalUrl(`/referral-boxes/templates/${templateId}`),
      true,
      true
    )

    if (error.value) {
      throw error.value
    }

    return data.value?.csi_content ?? null
  }

  /**
   * Verify a referral box template by UUID.
   *
   * @param {string} templateId
   * @return {Promise<ReferralBoxTemplate | null>}
   */
  public static async verifyReferralBoxTemplate(
    templateId: string
  ): Promise<ReferralBoxTemplate | null> {
    const { data, error } = await fetcher.get<CsiResponse<ReferralBoxTemplate>>(
      toPortalUrl(`/referral-boxes/templates/${templateId}/verify`)
    )

    if (error.value) {
      throw error.value
    }

    return data.value?.csi_content ?? null
  }

  /**
   * Use a referral box template by code.
   *
   * @param code Referral box template code
   * @param referrerSubscriberId Referrer subscriber ID
   * @param referredSubscriberId Referred subscriber ID
   * @returns {Promise<ReferralBox | null>}
   */
  public static async useReferralBoxTemplate(
    code: string,
    referrerSubscriberId: string,
    referredSubscriberId: string
  ): Promise<ReferralBox | null> {
    const { data, error } = await fetcher.post<CsiResponse<ReferralBox>>(
      toPortalUrl(`/referral-boxes/templates/use`),
      {
        referrer_subscriber_id: referrerSubscriberId,
        referred_subscriber_id: referredSubscriberId,
        code: code,
      },
      true,
      true
    )

    if (error.value) {
      throw error.value
    }

    return data.value?.csi_content ?? null
  }

  /**
   * Verify a referral box by UUID.
   *
   * @param {string} uuid
   * @returns {Promise<ReferralBox | null>}
   */
  public static async verifyReferralBox(
    uuid: string
  ): Promise<ReferralBox | null> {
    const { data, error } = await fetcher.get<CsiResponse<ReferralBox>>(
      toPortalUrl(`/referral-boxes/${uuid}/verify`)
    )

    if (error.value) {
      throw error.value
    }

    return data.value?.csi_content ?? null
  }

  /**
   * Use a referral box by code.
   *
   * @param code Referral box code
   * @returns {Promise<ReferralBox | null>}
   */
  public static async useReferralBox(
    code: string
  ): Promise<ReferralBox | null> {
    const { data, error } = await fetcher.post<CsiResponse<ReferralBox>>(
      toPortalUrl(`/referral-boxes/use`),
      { code },
      true,
      true
    )

    if (error.value) {
      throw error.value
    }

    return data.value?.csi_content ?? null
  }

  /**
   * Generate plan prizes dynamically from referral box prizes.
   *
   * @param {ReferralBox} referralBox
   * @returns {Array<RouletteReferralBoxPrize>}
   */
  public static generateReferralBoxPrizes(
    referralBox: ReferralBox
  ): Array<RouletteReferralBoxPrize> {
    const mainPrize: RouletteReferralBoxPrize = {
      title: `Win ${i18n.global.n(5000, 'currency')}`,
      prizes_code: 5000,
      type: 'cash',
      value: 5000,
      plan: '',
      image: {
        src: `/assets/images/prizes/instant-wins/svg/instant-prizes-5000-16_9.svg`,
        alt: `Win ${i18n.global.n(5000, 'currency')}`,
      },
    }

    const additionalPrizes: RouletteReferralBoxPrize[] = [
      { amount: '1_50', title: 'Win £1.50 Credit' },
      { amount: '50p', title: 'Win 50p Credit' },
      { amount: '1', title: 'Win £1 Credit' },
      { amount: '2', title: 'Win £2 Credit' },
    ].map((prize) => {
      return {
        title: prize.title,
        prizes_code: 0,
        type: 'credit',
        value: 0,
        plan: '',
        image: {
          src: `/assets/images/prizes/instant-wins/svg/instant-prizes-credit-${prize.amount}-16_9.svg`,
          alt: prize.title,
        },
      }
    })

    const referralBoxPrizes = referralBox.prizes
      .map((prize: ReferralBoxPrize) => {
        if (isReferralBoxPrizeCredit(prize)) {
          const creditPrize = prize.prizeable as ReferralBoxPrizeableCredit
          const amount = creditPrize.amount / 100

          let prizeValueString = amount.toString()

          if (amount === 1.5) {
            prizeValueString = '1_50'
          }

          // temp
          if (prizeValueString.includes('.')) {
            prizeValueString = `${Number(prizeValueString) * 100}p`
          }

          return {
            title: `Win ${i18n.global.n(amount, 'currency')} Credit`,
            prizes_code: amount,
            type: 'credit',
            value: amount,
            plan: '',
            image: {
              // use SVG plugin to get the image in instant wins
              src: `/assets/images/prizes/instant-wins/svg/instant-prizes-credit-${prizeValueString}-16_9.svg`,
              alt: `Win ${i18n.global.n(amount, 'currency')} Credit`,
            },
          }
        } else if (isReferralBoxPrizePlan(prize)) {
          const planPrize = prize.prizeable as ReferralBoxPrizeablePlan
          const platformId = planPrize.platform_id

          // Generate title based on platform_id or fallback to prizeable data
          let title = planPrize.label || 'Discount'
          let imageSrc =
            '/assets/images/prizes/referral-box/vip-membership-10.webp'

          if (platformId.includes('10')) {
            title = '10% Discount'
          } else if (platformId.includes('25')) {
            title = '25% Discount'
            imageSrc =
              '/assets/images/prizes/referral-box/vip-membership-25.webp'
          } else if (
            platformId.includes('50') ||
            platformId.includes('fixed')
          ) {
            title = '50% Discount'
            imageSrc =
              '/assets/images/prizes/referral-box/vip-membership-50.webp'
          }

          let randomPrizeCode = 0

          // Ensure we always emit a non-zero numeric code
          do {
            const parsedPrizeCode = Number.parseInt(getRandomNumber(6), 16)
            randomPrizeCode = Number.isNaN(parsedPrizeCode)
              ? 0
              : parsedPrizeCode
          } while (randomPrizeCode === 0)

          return {
            title,
            prizes_code: randomPrizeCode,
            type: 'sub_discount',
            value: 0,
            // Pass the frontend plan code so the checkout can resolve it correctly
            plan: planPrize.code,
            image: {
              src: imageSrc,
              alt: title,
            },
          }
        }

        return null
      })
      .filter((prize): prize is RouletteReferralBoxPrize => prize !== null)

    return [
      ...referralBoxPrizes,
      ...(referralBox.box_template_id === 4 || referralBox.box_template_id === 5
        ? [mainPrize, ...additionalPrizes]
        : []),
    ]
  }
}
